// Redux
import { createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from 'redux/store'

// Local
import findOrCreateMember from './utils/shared/findOrCreateMember'
import createDraftTransaction from './utils/apiv2/createDraftTransaction'
import finalizePayment from './utils/apiv2/finalizePayment'
import saveDedication from './utils/apiv2/saveDedication'
import saveConsentDetails from './utils/shared/saveConsentDetails'

// Types
import { FinalizeOrderArgs, FinalizeOrderResponse } from './utils/types'
import createOrder from './utils/orders/createOrder'
import getDesignationId from './utils/shared/getDesignationId'
import findOrCreateSupporter, { findSupporter } from './utils/shared/findOrCreateSupporter'
import saveResourceTrackingCodes from './utils/apiv2/saveResourceTrackingCodes'
import { getPurchaseFromOrder } from './utils/orders/getPurchaseFromOrder'
import { getPurchaseFromTransaction } from './utils/apiv2/getPurchaseFromTransaction'

import { omit } from 'lodash'
import { UAParser } from 'ua-parser-js'
import { setJwt } from 'redux/features/validation'
import { logger } from 'utils/logging'
import { getCookieValue } from 'utils/getCookieValue'

/*
 * Thunk responsible for finalizing a user order by calling a series of RTK slice
 * methods / API endpoints. This thunk should be called at the end of the
 * order workflow when all required data has been set into the store.
 *
 * We consider the creation of a draft transaction and finalization of the payment with the
 * payment processor to key steps for a transaction to be considered succesful.
 *
 * Because of this we structure this method in the following ways:
 *    1) Fetch the member (required to create draft transaction)
 *    2) Create draft transaction
 *    3) Finalize payment with payment provider
 *    4) In-parallel save dedication, consent details, designation, and resource tracking codes
 *
 *  Usage:
 *    const result = await dispatch(finalizeOrder())
 */
export const finalizeOrder = createAsyncThunk<
  FinalizeOrderResponse,
  FinalizeOrderArgs,
  { state: RootState }
>('order/finalize', async (args, { getState, dispatch }) => {
  // TODO [CL-45021]: Remove once META integration / resource tracking codes are improved.
  // Check to see if using instagram or facebook iOS in-app browser
  // Temporary fix for CL-45005 in order to track GDV from META integration
  const parser = new UAParser()
  const browser = parser.getBrowser()?.name
  const os = parser.getOS()?.name

  const isMeta = browser === 'Instagram' || browser === 'Facebook'

  // Overwrite referrer and c_src if os = iOS and browser = Instagram or Facebook
  if (os === 'iOS' && isMeta) {
    args.referrer = browser
    args.c_src = args.c_src ?? `meta-${browser.toLowerCase()}`
  }

  // Get the store
  const store: RootState = getState()

  /**
   * Will be set to the returned draft transaction from APIv2
   */
  let transaction

  // Initialize some shared variables
  let member
  let supporter
  let purchase

  // Check if we should use the cart flow or not
  const isCart: boolean = !!args.cart_id

  // Create a context object for logging
  const logContext = {
    'classy-session-id': getCookieValue('classy-session-id'),
    url: window.location.href,
    referrer: args.referrer,
    campaign: args.cid,
    organization: args.oid,
    browser,
    isCart,
    paymentMethod: store.order.paymentDetails.type,
  }

  // Get member, which is required by both Orders and Transactions
  try {
    /**
     * Fetch the Member
     */
    member = await findOrCreateMember(store, dispatch)
  } catch (e) {
    logger(
      'error',
      new Error('Error finalizing order: Could not find or create member', { cause: e }),
      { context: logContext },
    )

    throw e
  }

  /**
   * Orders: If its a cart, create order using Orders API
   *
   * If this is successful, we'll show a success to the donor.
   */

  if (isCart) {
    let order

    try {
      supporter = await findOrCreateSupporter(store, args.oid, dispatch)
      order = await createOrder(store, dispatch, args, member, supporter)
      // orders api creates transactions in one shot so we don't need to secure with a jwt
    } catch (e) {
      logger('error', new Error('Error finalizing order: Could not create order', { cause: e }), {
        context: { ...logContext, order: order?.id },
      })

      throw e
    }

    try {
      await saveConsentDetails(store, dispatch, supporter.id)
    } catch (e) {
      // NOTE: We don't want to show a failure if this fails
      logger('error', new Error('Error saving consent details', { cause: e }), {
        context: { ...logContext, order: order?.id },
      })
    }

    purchase = getPurchaseFromOrder(order, store)
  } else {
    try {
      /**
       * Get Designation Id (either in store, default, or RLC override)
       */
      args.designationId = await getDesignationId(store, dispatch, args)

      /**
       * Create draft RDP / Transaction
       *
       * Create the draft transaction with any immediately available information
       * colleted from the user.
       *
       * If this and the finalization of the payment (below) succeed we'll show a sucess
       * to the donor.
       */
      const transactionResponse = await createDraftTransaction(store, dispatch, member, args)
      transaction = transactionResponse.transaction
      dispatch(setJwt(transactionResponse.transactionToken))

      /**
       * Finalize payment with the payment provider
       *
       * Finalize the payment with the appropriate payment processor.
       *
       * This is the most important part of the order finalization step. If this succeeds
       * then we will show a success to the donor EVEN if other calls (below) fail.
       *
       * If this fails, then we should not make the other calls below.
       */
      await finalizePayment(store, dispatch, transaction, args)
    } catch (e) {
      logger(
        'error',
        new Error('Error finalizing transaction: Could not finalize payment', { cause: e }),
        { context: logContext },
      )

      throw e
    }

    /**
     * Santiy check - This should not happen but we'll catch it if does.
     */
    if (!transaction) throw new Error('We were unable to create a transaction.')

    /**
     * Do everything else.
     *
     * If the payment has been finalized with the payment processor than we can
     * then finalize the transaction details with Classy's models.
     *
     * These queries will have automatic retries for failure but if they are still not succesful
     * then it's very important that we track these failures with observability tools
     * so that we can be aware of any potential issues.
     */
    try {
      await Promise.all([
        saveDedication(store, dispatch, transaction),
        saveConsentDetails(store, dispatch, transaction.supporter_id),
        saveResourceTrackingCodes(dispatch, transaction, args),
      ])
    } catch (e) {
      // NOTE: We don't want to show a failure if any of these fail
      logger('error', new Error('Error in post payment finalization steps', { cause: e }), {
        context: { ...logContext, transaction: transaction?.id },
      })
    }

    supporter = await findSupporter(store, args.oid, dispatch)
    purchase = getPurchaseFromTransaction(transaction, store)
  }

  // Log success
  logger('info', 'donation_success', {
    context: { ...logContext, donationId: purchase.id },
  })

  // Finally, return data needed for post conversion actions
  return {
    purchase: {
      ...purchase,
      recurring_end_date: args.recurringEndDate,
    },
    isCart,
    transaction: omit(transaction, ['mongo_queries_log', 'queries_log', 'resource_urn']),
  }
})
